import React from 'react'
import Head from 'next/head'
import clsx from 'clsx'

import { Text } from '@wartek-id/text'

import { PageContainer, DesktopPageHeader } from 'components/ResponsiveLayout'
import { URL_HELPDESK } from 'app/BCKSPage/constants'

import type { BreadcrumbItemDef } from 'components/ResponsiveLayout'

interface BCKSDesktopPageShellProps {
  /** Page title for the document head AND visible page heading */
  title?: string
  /** Final breadcrumb segment label (rendered as: Beranda > Seleksi KS > {pageLabel}) */
  pageLabel: string
  /** Optional href for the pageLabel breadcrumb item (null = current page, no link) */
  pageLabelHref?: string | null
  /** Content to render inside PageContainer (or directly if rawContent is true) */
  children: React.ReactNode
  /** Optional className on the outer wrapper div (e.g., background color) */
  className?: string
  /** Optional className on the content area below the header */
  contentClassName?: string
  /** If true, children are rendered WITHOUT PageContainer wrapping (for custom layouts like full-width backgrounds) */
  rawContent?: boolean
  /** If false, hides the visible page title heading (default: true) */
  showPageTitle?: boolean
}

/**
 * Shared desktop layout shell for all BCKS pages.
 *
 * Renders:
 * 1. DesktopPageHeader with breadcrumb (Beranda > Seleksi KS > {pageLabel})
 *    and helpdesk navLink
 * 2. PageContainer wrapping children (default width), or raw children if rawContent is true
 *
 * Breadcrumb is always 3 levels: Beranda > Seleksi KS > {pageLabel}
 * navLinks includes Bantuan linking to URL_HELPDESK
 */
const BCKSDesktopPageShell = ({
  title,
  pageLabel,
  pageLabelHref = null,
  children,
  className,
  contentClassName,
  rawContent = false,
  showPageTitle = true,
}: BCKSDesktopPageShellProps) => {
  const breadcrumbItems: BreadcrumbItemDef[] = [
    { label: 'Seleksi KS', href: '/bcks/invitation/final' },
    { label: pageLabel, href: pageLabelHref },
  ]

  const navLinks = [{ label: 'Bantuan', href: URL_HELPDESK, external: true }]

  return (
    <div className={clsx('min-h-screen bg-surface-subdued', className)}>
      {title && (
        <Head>
          <title>{title}</title>
        </Head>
      )}
      <DesktopPageHeader
        breadcrumbItems={breadcrumbItems}
        navLinks={navLinks}
      />
      {title && showPageTitle && (
        <PageContainer className="!pt-6 !pb-2">
          <Text as="h1" variant="heading-lg">
            {title}
          </Text>
        </PageContainer>
      )}
      {rawContent ? (
        <main className={contentClassName}>{children}</main>
      ) : (
        <main className={contentClassName}>
          <PageContainer>{children}</PageContainer>
        </main>
      )}
    </div>
  )
}

export { BCKSDesktopPageShell }
export type { BCKSDesktopPageShellProps }
